import { AnimatePresence, motion } from 'framer-motion'
import { Mail, MessageSquare, Send } from 'lucide-react'
import { type FormEvent, useState } from 'react'
import { isValidPhoneNumber } from 'react-phone-number-input'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { z } from 'zod'

import snootsLogo from '@/customer/assets/snoots-logo.png'
import { AuthErrorBanner } from '@/customer/components/auth/auth-error-banner'
import { OtpInput, useOtpFlow } from '@/customer/components/auth/otp-input'
import {
  VERIFICATION_SUCCESS_HOLD_MS,
  VerificationSuccessPanel,
} from '@/customer/components/auth/verification-success-panel'
import { CardRadioGroup } from '@/customer/components/card-radio-group'
import { EmailInput } from '@/customer/components/email-input'
import { FormField } from '@/customer/components/form/fields'
import { StackedHeadline } from '@/customer/components/stacked-headline'
import { Button } from '@/customer/components/ui/button'
import { PhoneInput } from '@/customer/components/ui/phone-input'
import { useDocumentTitle } from '@/customer/hooks/use-document-title'
import { authClient, isAuthError } from '@/customer/lib/auth'
import { routes } from '@/customer/routes'

const emailSchema = z.email('Please enter a valid email')
/**
 * `PhoneInput` emits E.164 or empty. `isValidPhoneNumber` runs the full
 * libphonenumber-js check (numbering plan, not just length) which saves
 * a round-trip vs. length-only validation.
 */
const phoneSchema = z.string().refine(isValidPhoneNumber, 'Please enter a valid phone number')

type SignInMethod = 'email' | 'phone'
type Step = 'form' | 'otp' | 'success'

const METHOD_OPTIONS = [
  { value: 'email', label: 'Email', icon: <Mail className="size-8" /> },
  { value: 'phone', label: 'SMS', icon: <MessageSquare className="size-8" /> },
] as const satisfies { value: SignInMethod; label: string; icon: React.ReactNode }[]

export type SignInValues = {
  method: SignInMethod
  email: string
  phone: string
}

const EMPTY_VALUES: SignInValues = { method: 'email', email: '', phone: '' }

/**
 * The marketing site links members here with their E.164 phone in a query
 * param. `URLSearchParams` decodes `+` as a space (form-encoding rule), so a
 * raw `+4477…` arrives as ` 4477…`. Strip all whitespace (libphonenumber
 * happily parses formatted numbers, but `PhoneInput`'s controlled value
 * expects bare E.164), restore the leading `+`, then only trust the value if
 * it's valid E.164 — a national-format param like `07700900000` would become
 * the invalid `+07700900000` and leave the field with a number the input
 * can't resolve to a country.
 */
function normalizePhoneParam(raw: string | null): string {
  const stripped = raw?.replace(/\s/g, '')
  if (!stripped) return ''
  const candidate = stripped.startsWith('+') ? stripped : `+${stripped}`
  return isValidPhoneNumber(candidate) ? candidate : ''
}

/**
 * Derives the form's initial identifier values, letting `email` / `phone`
 * query params (used by the marketing site's "sign in" links) override the
 * defaults. Falls back to `fallback` when a param is absent or empty.
 */
export function resolvePrefillValues(
  searchParams: URLSearchParams,
  fallback: SignInValues
): SignInValues {
  return {
    method: fallback.method,
    email: searchParams.get('email')?.trim() || fallback.email,
    phone: normalizePhoneParam(searchParams.get('phone')) || fallback.phone,
  }
}

/**
 * Sign-in flow, rendered as three conditional views on a single route:
 *
 *   1. `form` - method picker (email / SMS) + identifier input
 *   2. `otp`  - 6-digit code entry with resend
 *   3. `success` - brief tick animation before navigating to `/account`
 *
 * Everything lives in one component (no cross-page navigation for the
 * OTP step) so form state stays in scope across transitions. Views
 * render by plain conditional - we deliberately don't animate between
 * them, matching the identifier verification pages under `/account`.
 *
 * Auth is powered by better-auth's email OTP and phone number plugins.
 * Both channels reduce to a symmetric two-step: dispatch a code, then
 * verify it. `signIn.emailOtp` and `phoneNumber.verify` both create the
 * session on success - no separate `finalize` step.
 */
export function SignInPage({
  initialValues = EMPTY_VALUES,
  initialStep = 'form',
}: {
  initialValues?: SignInValues
  initialStep?: Step
} = {}) {
  useDocumentTitle('Sign in')
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()

  // Prefill the identifier from `?email=` / `?phone=` query params so members
  // arriving from the marketing site land with their input already filled in.
  // Lazy initialisers keep this once-only: the params seed initial state at
  // mount and are never re-read, so later URL changes don't clobber input.
  const [step, setStep] = useState<Step>(initialStep)
  const [method, setMethod] = useState<SignInMethod>(
    () => resolvePrefillValues(searchParams, initialValues).method
  )
  const [email, setEmail] = useState(() => resolvePrefillValues(searchParams, initialValues).email)
  const [phone, setPhone] = useState(() => resolvePrefillValues(searchParams, initialValues).phone)
  // Toggles validation error copy on the identifier input. Silent until
  // the user attempts submit, then stays live so they get immediate
  // feedback while correcting. Reset on method switch so stale
  // validation chrome doesn't leak across channels.
  const [showFieldError, setShowFieldError] = useState(false)
  // Auth-level error from the form-step dispatch (bad identifier,
  // rate-limited, etc). OTP-step errors are owned by `useOtpFlow`
  // inside `<SignInOtpStep>`.
  const [dispatchErrorCode, setDispatchErrorCode] = useState<string | null>(null)
  const [isSubmitting, setIsSubmitting] = useState(false)

  const identifier = method === 'email' ? email : phone
  const validation =
    method === 'email' ? emailSchema.safeParse(email) : phoneSchema.safeParse(phone)
  const fieldError = validation.success
    ? null
    : (validation.error.issues[0]?.message ?? 'Invalid input')
  const canSubmit = validation.success && !isSubmitting

  function changeMethod(next: SignInMethod) {
    setMethod(next)
    setShowFieldError(false)
    setDispatchErrorCode(null)
  }

  async function handleSubmit(e: FormEvent) {
    e.preventDefault()
    setShowFieldError(true)
    setDispatchErrorCode(null)
    if (!validation.success) return

    setIsSubmitting(true)
    try {
      const { data, error } =
        method === 'email'
          ? await authClient.emailOtp.sendVerificationOtp({ email, type: 'sign-in' })
          : await authClient.phoneNumber.sendOtp({ phoneNumber: phone })
      if (error) throw error
      if (!data) throw new Error('Missing data')
    } catch (err) {
      setDispatchErrorCode(isAuthError(err) ? err.code : 'UNKNOWN')
      return
    } finally {
      setIsSubmitting(false)
    }

    setStep('otp')
  }

  return (
    <main className="mx-auto flex max-w-xl flex-col gap-10 px-6 pt-4 md:gap-16 md:pt-8">
      <header className="flex w-full items-center justify-center">
        <img src={snootsLogo} alt="Snoots" className="h-8" />
      </header>
      <StackedHeadline
        lines={[
          { text: 'Welcome', color: 'yellow' },
          { text: 'back', color: 'baby-blue' },
        ]}
      />

      <AnimatePresence mode="wait">
        {step === 'form' ? (
          <motion.form
            key="form"
            onSubmit={handleSubmit}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.15 }}
            className="flex flex-col gap-8"
          >
            <FormField
              label="How would you like to sign in?"
              labelUsesId
              render={({ id }) => (
                <CardRadioGroup
                  value={method}
                  onValueChange={(value) => changeMethod(value as SignInMethod)}
                  options={METHOD_OPTIONS}
                  aria-labelledby={id}
                />
              )}
            />

            <FormField
              label={method === 'email' ? 'Email address' : 'Phone number'}
              description={`Enter your ${method === 'email' ? 'email address' : 'phone number'} and we'll send you a one-time code to sign in.`}
              errors={showFieldError && fieldError ? [fieldError] : []}
              render={({ id }) =>
                method === 'email' ? (
                  <EmailInput
                    id={id}
                    placeholder="jane.doe@example.com"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    aria-invalid={showFieldError && fieldError !== null}
                  />
                ) : (
                  <PhoneInput
                    id={id}
                    placeholder="7700 900000"
                    autoComplete="tel"
                    value={phone || undefined}
                    onChange={(value: string | undefined) => setPhone(value ?? '')}
                    error={showFieldError && fieldError !== null}
                  />
                )
              }
            />

            <AuthErrorBanner code={dispatchErrorCode} />

            <Button type="submit" disabled={!canSubmit} className="h-14 w-full">
              <Send className="size-5" />
              Send code
            </Button>
          </motion.form>
        ) : step === 'otp' ? (
          <motion.div key="otp" exit={{ opacity: 0 }} transition={{ duration: 0.15 }}>
            <SignInOtpStep
              identifier={identifier}
              method={method}
              email={email}
              phone={phone}
              onSuccess={() => {
                setStep('success')
                window.setTimeout(
                  () => navigate(routes.account.index),
                  VERIFICATION_SUCCESS_HOLD_MS
                )
              }}
              onUseDifferentMethod={() => setStep('form')}
            />
          </motion.div>
        ) : (
          <motion.div key="success">
            <VerificationSuccessPanel active={step === 'success'} />
          </motion.div>
        )}
      </AnimatePresence>
    </main>
  )
}

function SignInOtpStep({
  identifier,
  method,
  email,
  phone,
  onSuccess,
  onUseDifferentMethod,
}: {
  identifier: string
  method: SignInMethod
  email: string
  phone: string
  onSuccess: () => void
  onUseDifferentMethod: () => void
}) {
  const otp = useOtpFlow({
    sendCode: async () => {
      const { data, error } =
        method === 'email'
          ? await authClient.emailOtp.sendVerificationOtp({ email, type: 'sign-in' })
          : await authClient.phoneNumber.sendOtp({ phoneNumber: phone })
      if (error) throw error
      if (!data) throw new Error('Missing data')
    },
    verifyCode: async (code) => {
      const { error } =
        method === 'email'
          ? await authClient.signIn.emailOtp({ email, otp: code })
          : await authClient.phoneNumber.verify({ phoneNumber: phone, code })
      if (error) throw error
    },
    onSuccess,
  })

  return (
    <div className="flex flex-col gap-6">
      <p className="text-body-md text-foreground">
        We&apos;ve sent a 6-digit code to <span className="font-semibold">{identifier}</span>. Enter
        it below to continue.
      </p>

      <div className="flex flex-col items-center gap-8 py-2">
        <OtpInput value={otp.otpCode} onChange={otp.setOtpCode} disabled={otp.isVerifying} />
        <AuthErrorBanner code={otp.errorCode} />
      </div>

      <div className="flex flex-col items-center gap-2 text-body-sm text-muted-foreground">
        <div className="flex items-center gap-2">
          <span>Didn&apos;t get the code?</span>
          <button
            type="button"
            onClick={otp.resend}
            disabled={otp.secondsRemaining > 0}
            className="cursor-pointer font-semibold text-foreground underline underline-offset-4 disabled:cursor-not-allowed disabled:text-muted-foreground disabled:no-underline"
          >
            {otp.secondsRemaining > 0 ? `Resend in ${otp.secondsRemaining}s` : 'Resend'}
          </button>
        </div>
        <button
          type="button"
          onClick={onUseDifferentMethod}
          className="cursor-pointer font-semibold text-foreground underline underline-offset-4"
        >
          Use a different method
        </button>
      </div>
    </div>
  )
}
